/**
 * Le passage d'un écran de la démo à l'autre : un fondu croisé, pas une coupure.
 *
 * On s'appuie sur les transitions de vue du navigateur (`startViewTransition`) : il photographie
 * l'écran avant, applique la mise à jour, photographie l'après, et fond l'un dans l'autre — rien
 * n'est démonté ni remonté deux fois côté React (une copie React du vieil écran rejouerait tout
 * l'exercice). Navigateur sans cette API, ou mouvement réduit : la mise à jour se fait d'un coup,
 * comme avant. ⚠️ `flushSync` est indispensable : sans lui React applique la mise à jour APRÈS la
 * photo de l'après, et la transition ne montre rien.
 */
import { flushSync } from 'react-dom';

type WithVT = Document & {
  startViewTransition?: (cb: () => void) => { finished: Promise<void> };
};

/** La marque posée sur `<html>` le temps du fondu : le CSS ne parle qu'aux transitions de la démo. */
export const SWAP_MARK = 'jrDemoSwap';

export function canSwapScreens(doc: Document = document): boolean {
  if (typeof (doc as WithVT).startViewTransition !== 'function') return false;
  return !doc.defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Applique `update` dans un fondu croisé quand le navigateur sait le faire. */
export function swapScreens(update: () => void, doc: Document = document): void {
  if (!canSwapScreens(doc)) {
    update();
    return;
  }
  doc.documentElement.dataset[SWAP_MARK] = '1';
  const t = (doc as WithVT).startViewTransition!(() => flushSync(update));
  void t.finished.finally(() => delete doc.documentElement.dataset[SWAP_MARK]);
}
